import { useState, useEffect } from 'react'
import { Label } from '@/components/ui/label'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Checkbox } from '@/components/ui/checkbox'
import { Card } from '@/components/ui/card'
import { Truck, Users, MapPin, Route, Layers } from 'lucide-react'

interface ScopeStepProps {
  state: any
  updateState: (updates: any) => void
}

const SCOPE_TYPES = [
  {
    value: 'fleet',
    label: 'Entire Fleet',
    description: 'Apply this rule to all vehicles and drivers',
    icon: Layers,
  },
  {
    value: 'vehicle',
    label: 'Specific Vehicles',
    description: 'Select individual vehicles to monitor',
    icon: Truck,
  },
  {
    value: 'driver',
    label: 'Specific Drivers',
    description: 'Select individual drivers to monitor',
    icon: Users,
  },
  {
    value: 'geofence',
    label: 'Geofence-based',
    description: 'Monitor activity within specific geofences',
    icon: MapPin,
  },
  {
    value: 'route',
    label: 'Route-based',
    description: 'Monitor specific routes',
    icon: Route,
  },
]

export function ScopeStep({ state, updateState }: ScopeStepProps) {
  const [vehicles, setVehicles] = useState<any[]>([])
  const [drivers, setDrivers] = useState<any[]>([])
  const [geofences, setGeofences] = useState<any[]>([])
  const [routes, setRoutes] = useState<any[]>([])

  useEffect(() => {
    // Fetch available items based on scope type
    if (state.scope_type === 'vehicle') {
      fetchVehicles()
    } else if (state.scope_type === 'driver') {
      fetchDrivers()
    } else if (state.scope_type === 'geofence') {
      fetchGeofences()
    } else if (state.scope_type === 'route') {
      fetchRoutes()
    }
  }, [state.scope_type])

  const fetchVehicles = async () => {
    try {
      const response = await fetch('/api/vehicles')
      const data = await response.json()
      setVehicles(data.vehicles || [])
    } catch (error) {
      console.error('Failed to fetch vehicles:', error)
    }
  }

  const fetchDrivers = async () => {
    try {
      const response = await fetch('/api/drivers')
      const data = await response.json()
      setDrivers(data.drivers || [])
    } catch (error) {
      console.error('Failed to fetch drivers:', error)
    }
  }

  const fetchGeofences = async () => {
    try {
      const response = await fetch('/api/geofences')
      const data = await response.json()
      setGeofences(data || [])
    } catch (error) {
      console.error('Failed to fetch geofences:', error)
    }
  }

  const fetchRoutes = async () => {
    try {
      const response = await fetch('/api/routes')
      const data = await response.json()
      setRoutes(data.routes || [])
    } catch (error) {
      console.error('Failed to fetch routes:', error)
    }
  }

  const handleItemToggle = (itemId: string, checked: boolean) => {
    const currentIds = state.scope_selector?.ids || []
    const newIds = checked
      ? [...currentIds, itemId]
      : currentIds.filter((id: string) => id !== itemId)

    updateState({
      scope_selector: { ...state.scope_selector, ids: newIds },
    })
  }

  const renderItemSelection = () => {
    let items: any[] = []
    let itemType = ''

    switch (state.scope_type) {
      case 'vehicle':
        items = vehicles
        itemType = 'vehicle'
        break
      case 'driver':
        items = drivers
        itemType = 'driver'
        break
      case 'geofence':
        items = geofences
        itemType = 'geofence'
        break
      case 'route':
        items = routes
        itemType = 'route'
        break
      default:
        return null
    }

    if (items.length === 0) {
      return (
        <Card className="p-6 text-center text-muted-foreground">
          No {itemType}s available. Please create some first.
        </Card>
      )
    }

    return (
      <div className="space-y-2 max-h-64 overflow-y-auto">
        {items.map((item) => (
          <div key={item.id} className="flex items-center space-x-2 p-2 hover:bg-accent rounded">
            <Checkbox
              id={item.id}
              checked={(state.scope_selector?.ids || []).includes(item.id)}
              onCheckedChange={(checked) => handleItemToggle(item.id, checked as boolean)}
            />
            <Label htmlFor={item.id} className="flex-1 cursor-pointer">
              {item.name || item.vehicle_id || item.full_name || item.id}
            </Label>
          </div>
        ))}
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-medium mb-4">Scope</h3>
        <p className="text-sm text-muted-foreground mb-6">
          Define what this alert rule should monitor
        </p>
      </div>

      <div className="space-y-4">
        <Label>Select scope type</Label>
        <RadioGroup
          value={state.scope_type}
          onValueChange={(value) =>
            updateState({ scope_type: value, scope_selector: {} })
          }
        >
          {SCOPE_TYPES.map((type) => {
            const Icon = type.icon
            return (
              <div key={type.value} className="flex items-center space-x-2">
                <RadioGroupItem value={type.value} id={type.value} />
                <Label
                  htmlFor={type.value}
                  className="flex-1 cursor-pointer p-3 border rounded-lg hover:bg-accent"
                >
                  <div className="flex items-start gap-3">
                    <Icon className="h-5 w-5 mt-0.5 text-muted-foreground" />
                    <div className="flex-1">
                      <div className="font-medium">{type.label}</div>
                      <div className="text-sm text-muted-foreground">
                        {type.description}
                      </div>
                    </div>
                  </div>
                </Label>
              </div>
            )
          })}
        </RadioGroup>

        {/* Item Selection */}
        {state.scope_type !== 'fleet' && (
          <div className="space-y-3 pt-4">
            <Label>Select specific items</Label>
            {renderItemSelection()}
          </div>
        )}
      </div>
    </div>
  )
}
